import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Card, Row, Col, Tag, Button, Table, message, Spin, Image, Modal } from "antd";
import {
  ArrowLeftOutlined,
  FilePdfOutlined,
  FileOutlined,
  DownloadOutlined
} from "@ant-design/icons";
import moment from "moment";
import PayInvoiceButton from "../../../../components/PayInvoiceButton";
import PayPartialButton from "../../../../components/PayPartialButton";
import clientBillingApi from "../store/api";
import {
  buildFacturaPdfFilename,
  downloadPdfResponse
} from "../../../admin/facturas/helper";
import { shortId } from "helper/util";

const ClientFacturaDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const [factura, setFactura] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllRoutesModal, setShowAllRoutesModal] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    const fetchFacturaData = async () => {
      try {
        setLoading(true);

        // Fetch factura details - response is already unwrapped by axios interceptor
        const facturaData = await clientBillingApi.getFactura(id);
        console.log("Received factura data:", facturaData);
        setFactura(facturaData);

        // Fetch factura logs - response is already unwrapped by axios interceptor
        const logsData = await clientBillingApi.getFacturaLogs(id);
        console.log("Received logs data:", logsData);
        setLogs(logsData || []);
      } catch (error) {
        console.error("Error fetching factura data:", error);

        if (error.status === 403) {
          message.error("Access denied: You can only view your own facturas");
        } else if (error.status === 404) {
          message.error("Factura not found");
        } else {
          message.error(
            "Failed to load factura details: " + (error.message || "Unknown error")
          );
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchFacturaData();
    }
  }, [id]);

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "green";
      case "partial":
        return "gold";
      case "unpaid":
        return "default";
      case "overdue":
        return "red";
      case "cancelled":
        return { backgroundColor: "#595959", color: "#ffffff" };
      default:
        return "default";
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return "--";
    }
    return "$" + parseFloat(amount).toFixed(2);
  };

  const renderFileIcon = (mimeType) => {
    if (mimeType && mimeType.startsWith("image/")) {
      return <FileOutlined style={{ color: "#52c41a" }} />;
    } else if (mimeType === "application/pdf") {
      return <FilePdfOutlined style={{ color: "#f5222d" }} />;
    } else {
      return <FileOutlined style={{ color: "#1890ff" }} />;
    }
  };

  const handleBack = () => {
    history.push("/billing/facturas");
  };

  const handlePaymentSuccess = () => {
    // Refetch factura data to get updated status
    const fetchFacturaData = async () => {
      try {
        const facturaData = await clientBillingApi.getFactura(id);
        setFactura(facturaData);

        // Also refetch logs to see the payment log entries
        const logsData = await clientBillingApi.getFacturaLogs(id);
        setLogs(logsData || []);
      } catch (error) {
        console.error("Error refetching factura data:", error);
      }
    };

    fetchFacturaData();
  };

  const handleExportFacturaPdf = async () => {
    if (!id) return;

    try {
      const response = await clientBillingApi.exportFacturaPdf(id);
      const blob = new Blob([response], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.target = "_blank";
      const facturaIdShort = factura?.facturaId || shortId(id);
      link.download = `factura-${facturaIdShort}-${new Date().getTime()}.pdf`;
      window.open(link.href, "_blank");
    } catch (err) {
      console.log(err);
      message.error("Failed to export PDF");
    }
  };

  const handleDownloadFacturaPdf = async () => {
    if (!id) return;

    setDownloadingPdf(true);
    try {
      const response = await clientBillingApi.exportFacturaPdf(id);
      downloadPdfResponse(
        response,
        buildFacturaPdfFilename(factura?.facturaId || shortId(id))
      );
    } catch (err) {
      console.log(err);
      message.error("Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const logColumns = [
    {
      title: "Date",
      dataIndex: "timestamp",
      width: 180,
      render: (value) => moment(value).format("YYYY-MM-DD HH:mm:ss")
    },
    {
      title: "Event Type",
      dataIndex: "eventType",
      width: 150,
      render: (value) => value || "--"
    },
    {
      title: "Summary",
      dataIndex: "summary",
      render: (value) => value || "--"
    },
    {
      title: "User",
      dataIndex: "user",
      width: 120,
      render: (user) =>
        user
          ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email
          : "--"
    }
  ];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px"
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!factura) {
    return (
      <div className="factura-detail-page client">
        <Card>
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>Factura not found</p>
            <Button onClick={handleBack}>Back to Facturas</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="factura-detail-page client">
      <div className="content-wrapper">
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Back to Facturas
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadFacturaPdf}
              loading={downloadingPdf}
            >
              Download PDF
            </Button>
          </div>
          <h2 style={{ margin: 0 }}>Factura Details</h2>
        </div>

        {/* Basic Information - Simplified */}
        <Card title="Factura Information" style={{ marginBottom: 24 }}>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12}>
              <div>
                <strong>Factura ID:</strong>
                <div style={{ fontFamily: "monospace", fontSize: "16px", marginTop: 8 }}>
                  {factura.facturaId}
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div>
                <strong>Date:</strong>
                <div style={{ marginTop: 8, fontSize: "16px" }}>
                  {moment(factura.facturaDate).format("YYYY-MM-DD")}
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div>
                <strong>Due Date:</strong>
                <div style={{ marginTop: 8, fontSize: "16px" }}>
                  {factura.dueDate ? moment(factura.dueDate).format("YYYY-MM-DD") : "--"}
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div>
                <strong>Total Amount:</strong>
                <div
                  style={{
                    marginTop: 8,
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#1890ff"
                  }}
                >
                  {formatCurrency(factura.total)}
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div>
                <strong>Total Paid:</strong>
                <div
                  style={{
                    marginTop: 8,
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#52c41a"
                  }}
                >
                  {formatCurrency(factura.totalPaid)}
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div>
                <strong>Status:</strong>
                <div style={{ marginTop: 8 }}>
                  <Tag
                    color={
                      typeof getStatusColor(factura.status) === "string"
                        ? getStatusColor(factura.status)
                        : undefined
                    }
                    style={{
                      textTransform: "capitalize",
                      fontSize: "14px",
                      padding: "4px 12px",
                      ...(typeof getStatusColor(factura.status) === "object"
                        ? getStatusColor(factura.status)
                        : {})
                    }}
                  >
                    {factura.status}
                  </Tag>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div>
                <strong>Actions:</strong>
                <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button
                    type="default"
                    icon={<FilePdfOutlined />}
                    onClick={handleExportFacturaPdf}
                    size="default"
                  >
                    View PDF
                  </Button>
                  <Button
                    type="default"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadFacturaPdf}
                    loading={downloadingPdf}
                    size="default"
                  >
                    Download PDF
                  </Button>
                  <PayInvoiceButton
                    facturaId={factura.id}
                    facturaStatus={factura.status}
                    isClientSide={true}
                    size="default"
                    onSuccess={handlePaymentSuccess}
                    onError={(error) => {
                      console.error("Payment error:", error);
                    }}
                  />
                  {/* Adrian asked to remove this
                  <PayPartialButton
                    facturaId={factura.id}
                    totalAmount={parseFloat(factura.total || 0)}
                    paidAmount={parseFloat(factura.totalPaid || 0)}
                    facturaStatus={factura.status}
                    isClientSide={true}
                    size="default"
                    onSuccess={handlePaymentSuccess}
                    onError={(error) => {
                      console.error("Payment error:", error);
                    }} 
                  />
                  */}
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Routes Section */}
        {factura && factura.facturaDetails?.routes && (
          <Card title="Routes" style={{ marginBottom: 24 }}>
            <div
              className="routes-summary"
              style={{
                marginBottom: 16,
                padding: 12,
                background: "#f5f5f5",
                borderRadius: 4
              }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <strong>Total Routes: </strong>
                  {factura.facturaDetails.summary?.totalRoutes ||
                    factura.facturaDetails.routes.length}
                </Col>
                <Col span={8}>
                  <strong>Total Amount: </strong>$
                  {(factura.facturaDetails.summary?.totalRoutePrice || 0).toFixed(2)}
                </Col>
              </Row>
            </div>
            <div className="routes-list">
              {factura.facturaDetails.routes.slice(0, 5).map((route) => (
                <Card
                  key={route.routeId}
                  size="small"
                  className="route-card"
                  style={{
                    marginBottom: 12,
                    cursor: "pointer",
                    border: "1px solid #d9d9d9"
                  }}
                  onClick={() => {
                    // Navigate to route detail page (client routes section)
                    history.push(`/routes/${route.routeId}`);
                  }}
                  hoverable
                >
                  <Row gutter={16} align="middle">
                    <Col span={4}>
                      <div
                        className="route-thumbnail"
                        style={{
                          width: 60,
                          height: 60,
                          backgroundColor: "#f0f0f0",
                          borderRadius: 4,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          color: "#666"
                        }}
                      >
                        Route
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ fontWeight: 500 }}>
                        {route.routeId.substring(0, 8)}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>Route ID</div>
                    </Col>
                    <Col span={6}>
                      <div style={{ fontWeight: 500 }}>{route.totalStops} stops</div>
                      <div style={{ fontSize: "12px", color: "#666" }}>Total Stops</div>
                    </Col>
                    <Col span={6}>
                      <div style={{ fontWeight: 500, color: "#52c41a" }}>
                        ${route.finalPrice.toFixed(2)}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>Final Price</div>
                    </Col>
                  </Row>
                </Card>
              ))}
            </div>
            {factura.facturaDetails.routes.length > 5 && (
              <div style={{ textAlign: "center", marginTop: 16 }}>
                <Button type="primary" onClick={() => setShowAllRoutesModal(true)}>
                  See All Routes ({factura.facturaDetails.routes.length})
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Pool Packages Section */}
        {factura && factura.facturaDetails?.poolPackages && (
          <Card title="Pool Packages" style={{ marginBottom: 24 }}>
            <div
              className="pool-packages-summary"
              style={{
                marginBottom: 16,
                padding: 12,
                background: "#f5f5f5",
                borderRadius: 4
              }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <strong>Total Pool Packages: </strong>
                  {factura.facturaDetails.summary?.totalPoolPackages ||
                    factura.facturaDetails.poolPackages.length}
                </Col>
                <Col span={8}>
                  <strong>Total Amount: </strong>$
                  {(factura.facturaDetails.summary?.totalPoolPackagePrice || 0).toFixed(
                    2
                  )}
                </Col>
              </Row>
            </div>
            <div className="pool-packages-list">
              {factura.facturaDetails.poolPackages.map((poolPackage) => (
                <Card
                  key={poolPackage.packageId}
                  size="small"
                  className="pool-package-card"
                  style={{
                    marginBottom: 12,
                    border: "1px solid #d9d9d9"
                  }}
                >
                  <Row gutter={16} align="middle">
                    <Col span={4}>
                      <div
                        className="pool-package-thumbnail"
                        style={{
                          width: 60,
                          height: 60,
                          backgroundColor: "#e6f4ff",
                          borderRadius: 4,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          color: "#1890ff"
                        }}
                      >
                        Package
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ fontWeight: 500 }}>
                        {poolPackage.packageId.substring(0, 8)}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>Package ID</div>
                    </Col>
                    <Col span={6}>
                      <div style={{ fontWeight: 500 }}>{poolPackage.status}</div>
                      <div style={{ fontSize: "12px", color: "#666" }}>Status</div>
                    </Col>
                    <Col span={6}>
                      <div style={{ fontWeight: 500, color: "#52c41a" }}>
                        ${(poolPackage.amount || 0).toFixed(2)}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>Amount</div>
                    </Col>
                  </Row>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Charges Section */}
        {factura &&
          factura.facturaDetails?.charges &&
          factura.facturaDetails.charges.length > 0 && (
            <Card title="Charges" style={{ marginBottom: 24 }}>
              <div
                className="charges-summary"
                style={{
                  marginBottom: 16,
                  padding: 12,
                  background: "#f5f5f5",
                  borderRadius: 4
                }}
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <strong>Total Charges: </strong>
                    {factura.facturaDetails.charges.length}
                  </Col>
                  <Col span={8}>
                    <strong>Total Amount: </strong>$
                    {factura.facturaDetails.charges
                      .reduce((sum, item) => sum + (item.amount || 0), 0)
                      .toFixed(2)}
                  </Col>
                </Row>
              </div>
              <div className="charges-list">
                {factura.facturaDetails.charges.map((charge, index) => (
                  <Card
                    key={charge.id || index}
                    size="small"
                    className="charge-card"
                    style={{
                      marginBottom: 12,
                      border: "1px solid #d9d9d9"
                    }}
                  >
                    <Row gutter={16} align="middle">
                      <Col span={4}>
                        <div
                          className="charge-thumbnail"
                          style={{
                            width: 60,
                            height: 60,
                            backgroundColor: "#fff2e6",
                            borderRadius: 4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            color: "#fa8c16"
                          }}
                        >
                          Chg
                        </div>
                      </Col>
                      <Col span={10}>
                        <div style={{ fontWeight: 500 }}>
                          {charge.description || "Charge"}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>Description</div>
                      </Col>
                      <Col span={4}>
                        <div style={{ fontWeight: 500 }}>{charge.quantity || 1}</div>
                        <div style={{ fontSize: "12px", color: "#666" }}>Quantity</div>
                      </Col>
                      <Col span={6}>
                        <div style={{ fontWeight: 500, color: "#52c41a" }}>
                          ${(charge.amount || 0).toFixed(2)}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>Amount</div>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            </Card>
          )}

        {/* Entitlements Section */}
        {factura &&
          factura.facturaDetails?.entitlements &&
          factura.facturaDetails.entitlements.length > 0 && (
            <Card title="Entitlements" style={{ marginBottom: 24 }}>
              <div
                className="entitlements-summary"
                style={{
                  marginBottom: 16,
                  padding: 12,
                  background: "#f5f5f5",
                  borderRadius: 4
                }}
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <strong>Total Entitlements: </strong>
                    {factura.facturaDetails.entitlements.length}
                  </Col>
                  <Col span={8}>
                    <strong>Total Amount: </strong>$
                    {factura.facturaDetails.entitlements
                      .reduce((sum, item) => sum + (item.amount || 0), 0)
                      .toFixed(2)}
                  </Col>
                </Row>
              </div>
              <div className="entitlements-list">
                {factura.facturaDetails.entitlements.map((entitlement, index) => (
                  <Card
                    key={entitlement.id || index}
                    size="small"
                    className="entitlement-card"
                    style={{
                      marginBottom: 12,
                      border: "1px solid #d9d9d9"
                    }}
                  >
                    <Row gutter={16} align="middle">
                      <Col span={4}>
                        <div
                          className="entitlement-thumbnail"
                          style={{
                            width: 60,
                            height: 60,
                            backgroundColor: "#e6f7ff",
                            borderRadius: 4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            color: "#1890ff"
                          }}
                        >
                          Ent
                        </div>
                      </Col>
                      <Col span={10}>
                        <div style={{ fontWeight: 500 }}>
                          {entitlement.description || "Entitlement"}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>Description</div>
                      </Col>
                      <Col span={4}>
                        <div style={{ fontWeight: 500 }}>{entitlement.quantity || 1}</div>
                        <div style={{ fontSize: "12px", color: "#666" }}>Quantity</div>
                      </Col>
                      <Col span={6}>
                        <div style={{ fontWeight: 500, color: "#52c41a" }}>
                          ${(entitlement.amount || 0).toFixed(2)}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>Amount</div>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            </Card>
          )}

        {/* Tiers Section */}
        {factura &&
          factura.facturaDetails?.tiers &&
          factura.facturaDetails.tiers.length > 0 && (
            <Card title="Tiers" style={{ marginBottom: 24 }}>
              <div
                className="tiers-summary"
                style={{
                  marginBottom: 16,
                  padding: 12,
                  background: "#f5f5f5",
                  borderRadius: 4
                }}
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <strong>Total Tiers: </strong>
                    {factura.facturaDetails.tiers.length}
                  </Col>
                  <Col span={8}>
                    <strong>Total Amount: </strong>$
                    {factura.facturaDetails.tiers
                      .reduce((sum, item) => sum + (item.amount || 0), 0)
                      .toFixed(2)}
                  </Col>
                </Row>
              </div>
              <div className="tiers-list">
                {factura.facturaDetails.tiers.map((tier, index) => (
                  <Card
                    key={tier.id || index}
                    size="small"
                    className="tier-card"
                    style={{
                      marginBottom: 12,
                      border: "1px solid #d9d9d9"
                    }}
                  >
                    <Row gutter={16} align="middle">
                      <Col span={4}>
                        <div
                          className="tier-thumbnail"
                          style={{
                            width: 60,
                            height: 60,
                            backgroundColor: "#fff7e6",
                            borderRadius: 4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            color: "#fa8c16"
                          }}
                        >
                          Tier
                        </div>
                      </Col>
                      <Col span={10}>
                        <div style={{ fontWeight: 500 }}>
                          {tier.description || "Tier"}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>Description</div>
                      </Col>
                      <Col span={4}>
                        <div style={{ fontWeight: 500 }}>{tier.quantity || 1}</div>
                        <div style={{ fontSize: "12px", color: "#666" }}>Quantity</div>
                      </Col>
                      <Col span={6}>
                        <div style={{ fontWeight: 500, color: "#52c41a" }}>
                          ${(tier.amount || 0).toFixed(2)}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>Amount</div>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            </Card>
          )}

        {/* Files Section */}
        {factura &&
          factura.facturaDetails?.files &&
          factura.facturaDetails.files.length > 0 && (
            <Card title="Files" style={{ marginBottom: 24 }}>
              <div
                className="files-summary"
                style={{
                  marginBottom: 16,
                  padding: 12,
                  background: "#f5f5f5",
                  borderRadius: 4
                }}
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <strong>Total Files: </strong>
                    {factura.facturaDetails.files.length}
                  </Col>
                  <Col span={8}>
                    <strong>Total Size: </strong>
                    {(
                      factura.facturaDetails.files.reduce(
                        (sum, file) => sum + (file.size || 0),
                        0
                      ) / 1024
                    ).toFixed(1)}{" "}
                    KB
                  </Col>
                </Row>
              </div>
              <div className="files-list">
                {factura.facturaDetails.files.map((file, index) => (
                  <Card
                    key={file.id || index}
                    size="small"
                    className="file-card"
                    style={{
                      marginBottom: 12,
                      border: "1px solid #d9d9d9"
                    }}
                  >
                    <Row gutter={16} align="middle">
                      <Col span={4}>
                        <div
                          className="file-thumbnail"
                          style={{
                            width: 60,
                            height: 60,
                            backgroundColor: "#f0f0f0",
                            borderRadius: 4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "20px"
                          }}
                        >
                          {file.mimeType &&
                          file.mimeType.startsWith("image/") &&
                          file.thumbnailUrl ? (
                            <img
                              width={60}
                              height={60}
                              src={file.thumbnailUrl}
                              style={{ objectFit: "cover", borderRadius: 4 }}
                              alt={file.originalName || file.name}
                            />
                          ) : (
                            renderFileIcon(file.mimeType)
                          )}
                        </div>
                      </Col>
                      <Col span={14}>
                        <div style={{ fontWeight: 500 }}>
                          {file.originalName || file.name}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {file.mimeType} - {(file.size / 1024).toFixed(1)} KB
                        </div>
                        <div style={{ fontSize: "12px", color: "#999" }}>
                          Uploaded:{" "}
                          {file.uploadedAt
                            ? moment(file.uploadedAt).format("YYYY-MM-DD HH:mm")
                            : "Unknown"}
                        </div>
                      </Col>
                      <Col span={6}>
                        <Button
                          type="link"
                          size="small"
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ padding: 0, height: "auto" }}
                        >
                          View File
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            </Card>
          )}

        {/* Factura Logs */}
        <Card title="Activity Log" style={{ marginBottom: 24 }}>
          <Table
            columns={logColumns}
            dataSource={logs}
            rowKey="id"
            size="small"
            pagination={false}
            locale={{
              emptyText: "No activity recorded"
            }}
          />
        </Card>
      </div>

      {/* All Routes Modal */}
      <Modal
        title={`All Routes (${factura?.facturaDetails?.routes?.length || 0})`}
        open={showAllRoutesModal}
        onCancel={() => setShowAllRoutesModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowAllRoutesModal(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        <div className="routes-list" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {factura?.facturaDetails?.routes?.map((route) => (
            <Card
              key={route.routeId}
              size="small"
              className="route-card"
              style={{
                marginBottom: 12,
                cursor: "pointer",
                border: "1px solid #d9d9d9"
              }}
              onClick={() => {
                setShowAllRoutesModal(false);
                history.push(`/routes/${route.routeId}`);
              }}
              hoverable
            >
              <Row gutter={16} align="middle">
                <Col span={4}>
                  <div
                    className="route-thumbnail"
                    style={{
                      width: 60,
                      height: 60,
                      backgroundColor: "#f0f0f0",
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      color: "#666"
                    }}
                  >
                    Route
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontWeight: 500 }}>{route.routeId.substring(0, 8)}</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>Route ID</div>
                </Col>
                <Col span={6}>
                  <div style={{ fontWeight: 500 }}>{route.totalStops} stops</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>Total Stops</div>
                </Col>
                <Col span={6}>
                  <div style={{ fontWeight: 500, color: "#52c41a" }}>
                    ${route.finalPrice.toFixed(2)}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>Final Price</div>
                </Col>
              </Row>
            </Card>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default ClientFacturaDetail;
